import { useEffect, useRef, useState, useMemo } from "react";
import { ChevronDown, X, Check, Search } from "lucide-react";

/**
 * MultiSelect — dropdown con checkboxes. Mantiene una API CSV-friendly:
 *   value: string CSV ("all" o "VAL1,VAL2,VAL3")
 *   onChange: (csv: string) => void
 *   options: array of strings OR {value, label?, count?}
 *
 * Diseñado para mezclarse con los <select> nativos del dashboard sin romper layout.
 */
export default function MultiSelect({
  value,
  onChange,
  placeholder = "Seleccionar",
  options,
  allValue = "all",
  allLabel = "Todas",
  className = "",
  width = "100%",
  testid,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef(null);

  const norm = useMemo(() => (options || []).map((o) => {
    if (typeof o === "string") return { value: o, label: o, count: undefined, meta: undefined };
    if (o.label) return { value: o.value, label: o.label, count: o.count, meta: o.meta, qualified_rate: o.qualified_rate };
    return { value: o.value, label: o.value, count: o.count, meta: o.meta, qualified_rate: o.qualified_rate };
  }), [options]);

  const selected = useMemo(() => {
    if (!value) return [];
    // Accept string, CSV string, or array. Filter out the "all" sentinel so a
    // stray ["all"] from cleared state doesn't inflate the selected count.
    const raw = Array.isArray(value)
      ? value.map((s) => String(s))
      : String(value).split(",");
    return raw
      .flatMap((s) => String(s).split(","))
      .map((s) => s.trim())
      .filter((s) => s && s !== allValue);
  }, [value, allValue]);

  const selectedSet = new Set(selected);

  // Close on click outside
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return norm;
    return norm.filter((o) => String(o.label).toLowerCase().includes(q));
  }, [norm, query]);

  const toggle = (val) => {
    const next = new Set(selected);
    if (next.has(val)) next.delete(val); else next.add(val);
    if (next.size === 0) onChange(allValue);
    else onChange([...next].join(","));
  };

  const selectAll = (e) => {
    e?.stopPropagation();
    // Populate the CSV with every option value. This visually checks
    // every item so the user can then uncheck the ones they want to
    // exclude (e.g. uncheck Yamaha to build a "todos excepto Yamaha"
    // filter with one extra click).
    const allVals = norm.map((o) => String(o.value)).filter(Boolean);
    if (allVals.length === 0) return;
    onChange(allVals.join(","));
  };

  const invert = (e) => {
    e?.stopPropagation();
    const allVals = norm.map((o) => String(o.value)).filter(Boolean);
    const nextSet = allVals.filter((v) => !selectedSet.has(v));
    if (nextSet.length === 0) onChange(allValue);
    else onChange(nextSet.join(","));
  };

  const clearAll = (e) => {
    e?.stopPropagation();
    setQuery("");
    onChange(allValue);
  };

  const triggerLabel = selected.length === 0
    ? placeholder
    : selected.length === 1
      ? selected[0]
      : `${selected.length} seleccionadas`;

  const isAll = selected.length === 0;
  const fmtCount = (n) => typeof n === "number" ? new Intl.NumberFormat("es-DO").format(n) : "";

  return (
    <div ref={ref} className={`multiselect ${className}`} style={{ position: "relative", width, minWidth: 160 }} data-testid={testid}>
      <button
        type="button"
        className="multiselect-trigger"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        data-testid={testid ? `${testid}-trigger` : undefined}
      >
        <span className="ms-label">
          <span className="ms-placeholder">{placeholder}:</span>{" "}
          <span className={`ms-value ${isAll ? "is-all" : ""}`}>{isAll ? allLabel : triggerLabel}</span>
        </span>
        {!isAll && (
          <span
            className="ms-clear"
            role="button"
            tabIndex={0}
            onClick={clearAll}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") clearAll(e); }}
            title="Limpiar"
            aria-label="Limpiar selección"
          >
            <X size={11} strokeWidth={2.5}/>
          </span>
        )}
        <ChevronDown size={14} className={`ms-chev ${open ? "is-open" : ""}`}/>
      </button>

      {open && (
        <div className="multiselect-pop" role="listbox">
          {norm.length > 8 && (
            <div className="ms-search">
              <Search size={12}/>
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Buscar..."
                autoFocus
              />
            </div>
          )}
          {norm.length > 1 && (
            <div className="ms-quick" role="group" aria-label="Acciones rápidas">
              <button
                type="button"
                className="ms-quick-btn"
                onClick={selectAll}
                data-testid={testid ? `${testid}-select-all` : undefined}
                title="Marcar todos los elementos"
              >
                Seleccionar todos
              </button>
              <button
                type="button"
                className="ms-quick-btn"
                onClick={invert}
                disabled={selected.length === 0}
                data-testid={testid ? `${testid}-invert` : undefined}
                title="Invertir la selección actual"
              >
                Invertir
              </button>
              <button
                type="button"
                className="ms-quick-btn"
                onClick={clearAll}
                disabled={selected.length === 0}
                data-testid={testid ? `${testid}-clear-quick` : undefined}
                title="Limpiar selección"
              >
                Limpiar
              </button>
            </div>
          )}
          {filtered.length === 0 && (
            <div className="ms-empty">Sin resultados</div>
          )}
          <ul className="ms-list">
            {filtered.map((o) => {
              const isSel = selectedSet.has(String(o.value));
              return (
                <li key={o.value}>
                  <button
                    type="button"
                    className={`ms-option ${isSel ? "is-selected" : ""}`}
                    onClick={() => toggle(String(o.value))}
                    data-testid={testid ? `${testid}-opt-${o.value}` : undefined}
                  >
                    <span className={`ms-check ${isSel ? "is-checked" : ""}`}>
                      {isSel && <Check size={11} strokeWidth={3}/>}
                    </span>
                    <span className="ms-opt-label truncate" title={o.label}>{o.label}</span>
                    {typeof o.qualified_rate === "number" && (
                      <span
                        className="ms-opt-rate"
                        title="Tasa de calificación"
                        style={{
                          fontSize: 10, fontWeight: 800,
                          color: o.qualified_rate >= 15 ? "#065f46" : o.qualified_rate >= 5 ? "#92400e" : "#991b1b",
                          background: o.qualified_rate >= 15 ? "#d1fae5" : o.qualified_rate >= 5 ? "#fef3c7" : "#fee2e2",
                          padding: "1px 6px", borderRadius: 999, flexShrink: 0, marginLeft: 4,
                        }}
                      >
                        {o.qualified_rate.toFixed(1)}%
                      </span>
                    )}
                    {o.count !== undefined && o.count > 0 && (
                      <span className="ms-opt-count">{fmtCount(o.count)}</span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
          {selected.length > 0 && (
            <div className="ms-footer">
              <button type="button" className="ms-clear-btn" onClick={clearAll} data-testid={testid ? `${testid}-clear` : undefined}>
                <X size={11}/> Limpiar ({selected.length})
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
